import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import useTriggerOnDatabaseUpdateStore, {
  getTriggerOnDatabaseUpdateStore,
  TRIGGER_TYPE_RECORD_CREATED,
  TRIGGER_TYPE_ANY_UPDATE,
  TRIGGER_TYPE_SPECIFIC_COLUMN,
} from './useTriggerOnDatabaseUpdateStore';
import { send_component_save_request, send_request } from '../../utils/Request';
import { getCurrentTime } from '../../utils/getCurrentTime';
import checkKey from '../../utils/CheckKey';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import CustomPaper from '../common/CustomPaper';
import './TriggerOnDatabaseUpdateStyle.css';

const LoadingSkeleton = ({ width, marginBottom = 0, style }) => (
  <Skeleton animation="wave" height={24} width={width} style={{ marginBottom, ...style }} />
);

const TriggerOnDatabaseUpdateTemplate = (props) => {
  const [loading, setLoading] = useState(true);
  const [loadingDatabaseList, setLoadingDatabaseList] = useState(false);
  const [loadingColumnList, setLoadingColumnList] = useState(false);
  const [databaseList, setDatabaseList] = useState([]);
  const [columnList, setColumnList] = useState([]);
  const [databaseName, setDatabaseName] = useState('');
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getTriggerOnDatabaseUpdateStore(props.component_id), [props.component_id]);

  const data = store((s) => s.data);
  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);

  const targetDatabaseId = data.targetDatabaseId;
  const triggerType = data.triggerType || TRIGGER_TYPE_ANY_UPDATE;
  const monitoredColumnId = data.monitoredColumnId;

  const fetchColumns = useCallback(async (databaseId) => {
    if (!databaseId) return;
    setLoadingColumnList(true);
    try {
      const response = await send_request(`database/get_columns/${databaseId}`, '', {}, 'GET');
      if (response?.data) {
        setColumnList(response.data);
        store.getState().setColumnList(response.data);
      }
    } catch (error) {
      console.error('Error fetching columns:', error);
    } finally {
      setLoadingColumnList(false);
    }
  }, []);

  const handleSave = useCallback(() => {
    const lastModified = getCurrentTime();
    const storeData = store.getState().getTemplate().data;
    const saveData = {
      componentData: {
        data: storeData,
        name: componentName,
        lastModified: lastModified,
        targetDatabaseId: storeData.targetDatabaseId,
      },
      componentId: props.component_id,
      type: 'trigger_on_database_update',
      description: componentDescription,
      status,
    };

    send_component_save_request(
      'component-service/trigger_on_database_update/data',
      saveData,
      '',
      'POST'
    )
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(saveData);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        console.error('Error saving component:', err);
        throw err;
      });
  }, [componentName, componentDescription, status, props]);

  // componentDidMount
  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);

      const storeState = store.getState();
      const existingDbList = storeState.data.databaseList;
      if (!existingDbList || existingDbList.length === 0) {
        send_request(`database/get_databases`, '', {}, 'GET')
          .then((response) => {
            if (response?.data) {
              const dbList = response.data;
              setDatabaseList(dbList);
              store.getState().setDatabaseList(dbList);

              const loadedTargetDbId = store.getState().data.targetDatabaseId;
              if (loadedTargetDbId) {
                const selectedDb = dbList.find((db) => db.databaseId === loadedTargetDbId);
                if (selectedDb) {
                  setDatabaseName(selectedDb.name);
                }
                useProjectStore.setState({ targetDataBaseId: loadedTargetDbId });
                fetchColumns(loadedTargetDbId);
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching database list on prefill:', error);
          });
      } else {
        setDatabaseList(existingDbList);
        const loadedTargetDbId = storeState.data.targetDatabaseId;
        if (loadedTargetDbId) {
          const selectedDb = existingDbList.find((db) => db.databaseId === loadedTargetDbId);
          if (selectedDb) {
            setDatabaseName(selectedDb.name);
          }
          useProjectStore.setState({ targetDataBaseId: loadedTargetDbId });
          const existingColList = storeState.data.columnList;
          if (!existingColList || existingColList.length === 0) {
            fetchColumns(loadedTargetDbId);
          } else {
            setColumnList(existingColList);
          }
        }
      }

      return;
    }

    setLoading(true);

    const loadData = async () => {
      try {
        const [databaseResponse, componentResponse] = await Promise.all([
          send_request(`database/get_databases`, '', {}, 'GET'),
          send_request(
            `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
            '',
            {}
          ),
        ]);

        if (databaseResponse?.data) {
          const dbList = databaseResponse.data;
          setDatabaseList(dbList);
          store.getState().setDatabaseList(dbList);
        }

        if (componentResponse?.data) {
          const component = componentResponse.data.components?.[0] || componentResponse.data;
          store.getState().setComponentData(component);
          store.setState({ loaded: true });
          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(component.description);
          props.onLastModifiedChanged(store.getState().lastModified);

          const loadedTargetDatabaseId =
            store.getState().data.targetDatabaseId;
          if (loadedTargetDatabaseId && databaseResponse?.data) {
            const selectedDb = databaseResponse.data.find(
              (db) => db.databaseId === loadedTargetDatabaseId
            );
            if (selectedDb) {
              setDatabaseName(selectedDb.name);
              fetchColumns(loadedTargetDatabaseId);
            }
            useProjectStore.setState({ targetDataBaseId: loadedTargetDatabaseId });
          }
        }

      } catch (error) {
        console.error('Error loading component data:', error);
      } finally {
        setLoading(false);
        props.setChildTemplateLoaded(true);
      }
    };

    loadData();
  }, []);

  const handleKeyChange = useCallback((e) => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setOutputPlaceholderKey(placeholderValue);

    const storeData = store.getState().data;
    const value = checkKey(placeholderValue, storeData);
    setCheckKeyError(value[0]);
    setErrorMessage(value[1]);
  }, []);

  const handleDatabaseChange = useCallback(
    (event, newValue) => {
      if (!newValue) {
        store.getState().setTargetDatabaseId(null);
        useProjectStore.setState({ targetDataBaseId: null });
        setDatabaseName('');
        setColumnList([]);
        return;
      }
      store.getState().setTargetDatabaseId(newValue.databaseId);
      useProjectStore.setState({ targetDataBaseId: newValue.databaseId });
      setDatabaseName(newValue.name);
      fetchColumns(newValue.databaseId);
    },
    [fetchColumns]
  );

  const handleTriggerTypeChange = useCallback((event) => {
    store.getState().setTriggerType(event.target.value);
  }, []);

  const handleColumnChange = useCallback((event, newValue) => {
    if (!newValue) {
      store.getState().setMonitoredColumn(null, null);
      return;
    }
    store.getState().setMonitoredColumn(newValue.columnId, newValue.name);
  }, []);

  const refreshDatabaseList = useCallback(async () => {
    setLoadingDatabaseList(true);
    try {
      const response = await send_request(`database/get_databases`, '', {}, 'GET');
      if (response?.data) {
        setDatabaseList(response.data);
        store.getState().setDatabaseList(response.data);

        const currentTargetDbId = store.getState().data.targetDatabaseId;
        if (currentTargetDbId) {
          const selectedDb = response.data.find((db) => db.databaseId === currentTargetDbId);
          if (selectedDb) {
            setDatabaseName(selectedDb.name);
          } else {
            setDatabaseName('');
            store.getState().setTargetDatabaseId(null);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing database list:', error);
    } finally {
      setLoadingDatabaseList(false);
    }
  }, []);

  const selectedDatabase = databaseList.find((db) => db.databaseId === targetDatabaseId);
  const selectedColumn = columnList.find((col) => col.columnId === monitoredColumnId);

  if (loading) {
    return <IndividualComponentLoader />;
  }

  return (
    <CustomPaper className="trigger-db-update-container">
      <Grid container spacing={3}>
        {/* Database Selection Section */}
        <Grid item xs={12} className="trigger-db-update-section">
          <Typography className="trigger-db-update-label">Select table to monitor</Typography>
          <Typography className="trigger-db-update-helper-text" style={{ marginBottom: 12 }}>
            Choose the table that this trigger will monitor for changes.
          </Typography>
          <Autocomplete
            options={databaseList}
            getOptionLabel={(option) => option.name || option.databaseId || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select a table"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.databaseId}>
                {option.name || option.databaseId}
              </li>
            )}
            onChange={handleDatabaseChange}
            onOpen={refreshDatabaseList}
            value={selectedDatabase || null}
            loading={loadingDatabaseList}
            loadingText={
              <div style={{ padding: '8px' }}>
                <LoadingSkeleton width="80%" marginBottom={6} />
                <LoadingSkeleton width="60%" marginBottom={6} />
                <LoadingSkeleton width="70%" />
              </div>
            }
            style={{ maxWidth: 400 }}
          />
          {databaseName && targetDatabaseId && (
            <Link
              to={`/databases/database_management/${targetDatabaseId}`}
              target="_blank"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                marginTop: 12,
                fontSize: '0.9em',
                color: 'var(--color-text-primary)',
              }}
            >
              <OpenInNewIcon style={{ height: 14, marginRight: 4 }} />
              Open {databaseName} in a new tab
            </Link>
          )}
        </Grid>

        {/* Trigger Type Section */}
        {targetDatabaseId && (
          <Grid item xs={12} className="trigger-db-update-section">
            <Typography className="trigger-db-update-label">Trigger when</Typography>
            <Typography className="trigger-db-update-helper-text" style={{ marginBottom: 12 }}>
              Choose what type of database change should trigger this workflow.
            </Typography>
            <RadioGroup
              value={triggerType}
              onChange={handleTriggerTypeChange}
              className="trigger-type-radio-group"
            >
              <FormControlLabel
                value={TRIGGER_TYPE_RECORD_CREATED}
                control={<Radio size="small" />}
                label={
                  <span className="trigger-type-label-with-icon">
                    <AddCircleOutlineIcon className="trigger-type-icon" />A new record is created
                  </span>
                }
              />
              <FormControlLabel
                value={TRIGGER_TYPE_ANY_UPDATE}
                control={<Radio size="small" />}
                label={
                  <span className="trigger-type-label-with-icon">
                    <EditNoteIcon className="trigger-type-icon" />
                    Any column in an existing record is updated
                  </span>
                }
              />
              <FormControlLabel
                value={TRIGGER_TYPE_SPECIFIC_COLUMN}
                control={<Radio size="small" />}
                label={
                  <span className="trigger-type-label-with-icon">
                    <ViewColumnIcon className="trigger-type-icon" />A specific column in an
                    existing record is updated
                  </span>
                }
              />
            </RadioGroup>
          </Grid>
        )}

        {/* Column Selection Section (only shown for SPECIFIC_COLUMN trigger type) */}
        {targetDatabaseId && triggerType === TRIGGER_TYPE_SPECIFIC_COLUMN && (
          <Grid item xs={12} className="trigger-db-update-section">
            <Typography className="trigger-db-update-label">Select column to monitor</Typography>
            <Typography className="trigger-db-update-helper-text" style={{ marginBottom: 12 }}>
              The workflow will only trigger when this specific column is updated.
            </Typography>
            <Autocomplete
              options={columnList}
              getOptionLabel={(option) => option.name || option.columnId || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select a column"
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.columnId}>
                  {option.name || option.columnId}
                </li>
              )}
              onChange={handleColumnChange}
              value={selectedColumn || null}
              loading={loadingColumnList}
              loadingText={
                <div style={{ padding: '8px' }}>
                  <LoadingSkeleton width="80%" marginBottom={6} />
                  <LoadingSkeleton width="60%" marginBottom={6} />
                  <LoadingSkeleton width="70%" />
                </div>
              }
              style={{ maxWidth: 400 }}
            />
          </Grid>
        )}

        {/* Output Placeholder Section */}
        <Grid item xs={12} className="trigger-db-update-section">
          <Typography className="trigger-db-update-label">Output placeholder</Typography>
          <Typography className="trigger-db-update-helper-text" style={{ marginBottom: 12 }}>
            The {triggerType === TRIGGER_TYPE_RECORD_CREATED ? 'new' : 'updated'} record will be
            output as a JSON string in this placeholder.
          </Typography>
          <W86PlaceholderTextField
            value={data.outputKey || ''}
            error={checkKeyError}
            helperText={checkKeyError ? errorMessage : null}
            placeholderText="Enter placeholder for record"
            handleChange={handleKeyChange}
            customStyleProps={{ maxWidth: 400 }}
          />
        </Grid>
      </Grid>
    </CustomPaper>
  );
};

export default TriggerOnDatabaseUpdateTemplate;
